import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSiteData } from "@/hooks/useSite";
import { LineReveal, Reveal, ParallaxImage, FadeIn } from "@/components/Motion";
import { photoPosition } from "@/lib/site";
import Marquee from "@/components/Marquee";
import Stamp from "@/components/Stamp";
import MethodSteps from "@/components/MethodSteps";

function HomeServiceRows({ services, photos }) {
  const floats = [photos.hero, photos.method, photos.services, photos.aboutSecond, photos.about];
  const floatRef = useRef(null);
  const [active, setActive] = useState(null);

  const onMove = (e) => {
    if (floatRef.current) {
      floatRef.current.style.left = `${e.clientX + 28}px`;
      floatRef.current.style.top = `${e.clientY - 100}px`;
    }
  };

  return (
    <div data-testid="home-services-list" className="relative" onMouseMove={onMove} onMouseLeave={() => setActive(null)}>
      {services.map((s, i) => (
        <Reveal key={s.id} delay={i * 0.04}>
          <Link
            to={`/prenota?servizio=${s.id}`}
            data-testid={`home-service-row-${s.id}`}
            onMouseEnter={() => setActive(i)}
            className="group grid grid-cols-12 items-center gap-3 lg:gap-6 py-6 lg:py-8 border-t border-taupe/30 px-2 lg:px-6 transition-colors duration-500 hover:bg-peach"
          >
            <span className="col-span-2 lg:col-span-1 font-display italic text-xl lg:text-2xl text-taupe">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="col-span-9 lg:col-span-10 font-display text-2xl lg:text-4xl leading-tight group-hover:translate-x-2 transition-transform duration-500">
              {s.name}
              <span className="ml-3 align-middle text-[10px] lg:text-[11px] uppercase tracking-widest2 font-sans text-taupe">
                {s.mode === "online" ? "Online" : "In studio"}
              </span>
            </span>
            <span className="hidden lg:flex col-span-1 justify-end">
              <ArrowUpRight size={22} className="text-taupe transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </Link>
        </Reveal>
      ))}
      <div className="border-t border-taupe/30" />
      <div
        ref={floatRef}
        aria-hidden="true"
        className={`pointer-events-none fixed z-40 hidden lg:block w-60 transition-opacity duration-300 ${active !== null ? "opacity-100" : "opacity-0"}`}
        data-testid="home-services-hover-preview"
      >
        {active !== null && floats[active] && (
          <img
            src={floats[active]}
            alt=""
            className="w-full aspect-[4/3] object-cover rounded-2xl rotate-2 shadow-soft"
          />
        )}
      </div>
    </div>
  );
}

function HeroSection({ settings }) {
  const hero = settings.content.hero;
  const c = settings.contact;
  return (
    <>
      <section className="relative min-h-[calc(100vh-0px)] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="max-w-[1440px] mx-auto w-full px-5 lg:px-12 pt-28 lg:pt-24 pb-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-6 xl:col-span-6">
            <FadeIn delay={0.1}>
              <p className="flex items-center gap-3 text-[11px] lg:text-xs uppercase tracking-widest2 text-deeptaupe font-medium">
                <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
                {hero.overline}
              </p>
            </FadeIn>

            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl xl:text-[6.4rem] leading-[0.95] tracking-tight mt-6 mb-8 text-charcoal">
              <LineReveal delay={0.25}>{hero.line1}</LineReveal>
              <LineReveal delay={0.45} className="text-deeptaupe italic">
                {hero.line2}
              </LineReveal>
            </h1>

            <FadeIn delay={0.8}>
              <p className="text-base lg:text-lg leading-relaxed text-charcoal/75 max-w-md">{hero.subtitle}</p>
            </FadeIn>

            <FadeIn delay={0.95}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mt-10">
                <Link
                  to={hero.ctaPrimaryLink || "/prenota"}
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-3 rounded-full bg-deeptaupe text-ivory text-[13px] sm:text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-charcoal"
                >
                  {hero.ctaPrimary}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to={hero.ctaSecondaryLink || "/chi-sono"}
                  data-testid="hero-cta-secondary"
                  className="link-underline inline-flex items-center gap-2 text-sm uppercase tracking-widest2 font-medium text-charcoal hover:text-deeptaupe transition-colors"
                >
                  {hero.ctaSecondary} <ArrowUpRight size={15} />
                </Link>
              </div>
            </FadeIn>

            <FadeIn delay={1.15}>
              <div className="flex items-center gap-6 mt-14 text-[13px] text-charcoal/70">
                <span className="flex items-center gap-2">
                  <MapPin size={14} className="text-taupe" /> {c.addressCity}, {c.addressCountry}
                </span>
                <span className="w-px h-4 bg-taupe/40" aria-hidden="true" />
                <span>In studio · Online</span>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative max-w-[560px] mx-auto lg:ml-auto mb-16 lg:mb-0">
              <div
                aria-hidden="true"
                className="absolute -top-8 -right-8 lg:-top-14 lg:-right-14 w-56 h-56 lg:w-80 lg:h-80 rounded-full bg-peach -z-10"
              />
              <div className="overflow-hidden rounded-t-[999px] rounded-b-[32px] aspect-[3/4] max-h-[76vh] w-full shadow-soft">
                <ParallaxImage
                  src={settings.photos.hero}
                  alt="Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista, sceglie prodotti freschi al supermercato"
                  className="w-full h-full"
                  priority
                  intensity={7}
                  position={photoPosition(settings, "hero")}
                />
              </div>
              <Stamp className="absolute -top-6 -left-6 lg:-top-10 lg:-left-16 w-24 h-24 lg:w-36 lg:h-36 z-10" />
              <FadeIn delay={1.2} className="absolute -bottom-6 right-0 lg:-right-4 z-10">
                <div className="flex items-center gap-3 bg-ivory/90 backdrop-blur-xl border border-taupe/25 rounded-2xl px-4 lg:px-5 py-3.5 shadow-[0_8px_32px_rgba(51,48,45,0.10)]">
                  <img src="/favicon.png" alt="" aria-hidden="true" className="w-9 h-9" />
                  <div>
                    <p className="font-display text-base lg:text-lg leading-tight text-charcoal">Dott.ssa Francesca Notarfrancesco</p>
                    <p className="text-[11px] uppercase tracking-widest2 text-deeptaupe">Biologa Nutrizionista</p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <Marquee />
    </>
  );
}

function IntroSection({ settings }) {
  const intro = settings.content.intro;
  return (
    <section className="py-20 lg:py-28 bg-sand/20" data-testid="intro-section">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-3">
          <Reveal>
            <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium lg:sticky lg:top-32">
              {intro.overline}
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">{intro.title}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-charcoal/75 max-w-2xl">{intro.text}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/chi-sono"
              data-testid="intro-about-link"
              className="link-underline inline-flex items-center gap-2 mt-10 text-sm uppercase tracking-widest2 font-medium text-deeptaupe"
            >
              Conosci Francesca <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-3 lg:col-start-10">
          <Reveal delay={0.1}>
            <div className="rounded-[28px] overflow-hidden aspect-[3/4] shadow-soft" data-testid="intro-photo">
              <ParallaxImage
                src={settings.photos.profile}
                alt="Ritratto di profilo di Dott.ssa Francesca Notarfrancesco"
                className="w-full h-full"
                position={photoPosition(settings, "profile")}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesSection({ settings, services }) {
  const svc = settings.content.services;
  const studioServices = services.filter((s) => s.mode === "studio" && s.active !== false);
  const all = studioServices.concat(services.filter((s) => s.mode === "online" && s.active !== false));
  return (
    <section className="pb-20 lg:pb-28" data-testid="services-overview">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
        <div className="flex items-end justify-between gap-6 mb-10 lg:mb-14">
          <Reveal>
            <div>
              <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-3">{svc.title}</p>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight">{svc.homeTitle || "Percorsi su misura, in studio e online"}</h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/servizi"
              data-testid="home-services-all-link"
              className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-widest2 font-medium text-deeptaupe link-underline shrink-0"
            >
              Tutti i servizi <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>

        <HomeServiceRows services={all} photos={settings.photos} />

        <div className="sm:hidden pt-8">
          <Link to="/servizi" data-testid="home-services-all-link-mobile" className="text-sm uppercase tracking-widest2 font-medium text-deeptaupe">
            Tutti i servizi →
          </Link>
        </div>
      </div>
    </section>
  );
}

function MethodSection({ settings }) {
  const statement = settings.content.method.statement || "";
  return (
    <section className="bg-charcoal text-ivory py-24 lg:py-36 grain relative" data-testid="home-method-section">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
        <Reveal>
          <p className="text-[11px] uppercase tracking-widest2 text-sand/80 font-medium mb-10 lg:mb-14">Il metodo K Nutrition</p>
        </Reveal>
        <Reveal>
          <blockquote className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.1] max-w-5xl mb-14 lg:mb-20">
            "{statement.split(":")[0]}:{" "}
            <em className="text-sand">{statement.split(":").slice(1).join(":").trim()}</em>"
          </blockquote>
        </Reveal>
        <MethodSteps steps={settings.content.method.steps} dark />
        <Reveal>
          <div className="mt-16 flex flex-col sm:flex-row items-start gap-6 justify-between border-t border-ivory/15 pt-10">
            <p className="text-ivory/75 max-w-md text-base lg:text-lg leading-relaxed">
              Un percorso pensato per durare nel tempo, passo dopo passo, con te.
            </p>
            <Link
              to="/il-metodo"
              data-testid="home-method-link"
              className="inline-flex items-center gap-2 rounded-full border border-ivory/30 text-ivory text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:bg-ivory hover:text-charcoal"
            >
              Scopri il metodo <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CtaSection({ settings }) {
  const cta = settings.content.cta;
  const words = (cta.title || "").split(" ");
  const last = words.pop();
  return (
    <section className="py-20 lg:py-28" data-testid="home-cta-section">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
        <Reveal>
          <div className="bg-deeptaupe text-ivory rounded-[36px] px-8 py-14 lg:px-20 lg:py-20 flex flex-col lg:flex-row lg:items-center justify-between gap-10 relative overflow-hidden grain">
            <div className="max-w-xl relative z-10">
              <p className="text-[11px] uppercase tracking-widest2 text-sand/90 font-medium mb-4">Prenota</p>
              <h2 className="font-display text-4xl lg:text-6xl leading-[1.05] tracking-tight">
                {words.join(" ")} <em className="text-sand">{last}</em>
              </h2>
              <p className="mt-5 text-ivory/75 leading-relaxed">{cta.text}</p>
            </div>
            <Link
              to={cta.buttonLink || "/prenota"}
              data-testid="home-cta-button"
              className="group inline-flex items-center gap-3 self-start lg:self-auto rounded-full bg-peach text-charcoal text-sm uppercase tracking-widest2 font-medium px-10 py-5 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ivory relative z-10"
            >
              {cta.button}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <span aria-hidden="true" className="absolute -right-10 -top-16 font-display italic text-[16rem] leading-none text-ivory/5 select-none">K</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CustomBlock({ block }) {
  return (
    <section className="py-20 lg:py-28" data-testid={`home-section-${block.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {block.image && (
          <Reveal>
            <div className="lg:col-span-5 rounded-[28px] overflow-hidden aspect-[4/3] shadow-soft" data-testid={`home-section-photo-${block.id}`}>
              <ParallaxImage src={block.image} alt={block.title || ""} className="w-full h-full" />
            </div>
          </Reveal>
        )}
        <div className={block.image ? "lg:col-span-7" : "lg:col-span-10 lg:col-start-2"}>
          <Reveal>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight">{block.title}</h2>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-charcoal/75 whitespace-pre-line">{block.text}</p>
            {block.ctaLabel && (
              <Link
                to={block.ctaUrl || "/prenota"}
                data-testid={`home-section-cta-${block.id}`}
                className="link-underline inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-widest2 font-medium text-deeptaupe"
              >
                {block.ctaLabel} <ArrowRight size={15} />
              </Link>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ContactStrip({ settings }) {
  const c = settings.contact;
  return (
    <section className="pb-24 lg:pb-32" data-testid="home-contact-section">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
        <Reveal>
          <div className="border-t border-taupe/30 pt-12 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-4">Dove siamo</p>
              <p className="text-charcoal/80 leading-relaxed">
                {c.addressStreet}
                <br />
                {c.addressCity}, {c.addressCountry}
              </p>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c.mapsQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-directions-link"
                className="link-underline inline-flex items-center gap-1.5 mt-4 text-sm text-deeptaupe font-medium"
              >
                Indicazioni <ArrowUpRight size={14} />
              </a>
            </div>
            <div className="md:col-span-4">
              <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-4">Contatti</p>
              <ul className="space-y-3 text-charcoal/80">
                <li>
                  <a href={`tel:${c.phoneHref}`} data-testid="home-phone-link" className="flex items-center gap-3 hover:text-deeptaupe transition-colors">
                    <Phone size={15} className="text-taupe" /> {c.phone}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${c.email}`} data-testid="home-email-link" className="flex items-center gap-3 hover:text-deeptaupe transition-colors">
                    <Mail size={15} className="text-taupe" /> {c.email}
                  </a>
                </li>
                <li>
                  <a
                    href={c.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="home-instagram-link"
                    className="flex items-center gap-3 hover:text-deeptaupe transition-colors"
                  >
                    <Instagram size={15} className="text-taupe" /> {c.instagramHandle}
                  </a>
                </li>
              </ul>
            </div>
            <div className="md:col-span-4">
              <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-4">Appuntamenti</p>
              <p className="text-charcoal/75 text-sm leading-relaxed mb-5">
                Le richieste vengono gestite direttamente tramite WhatsApp, con conferma della disponibilità da
                parte dello studio.
              </p>
              <Link
                to="/prenota"
                data-testid="home-contact-cta"
                className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-sm uppercase tracking-widest2 font-medium px-7 py-3.5 transition-all duration-300 hover:bg-deeptaupe hover:text-ivory"
              >
                Richiedi un appuntamento <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings, services } = useSiteData();
  const seo = settings.seo?.home || {};
  useSEO({
    title: seo.title || "K Nutrition — Nutrizionista a Salerno | Dott.ssa Francesca Notarfrancesco",
    description:
      seo.description ||
      "K Nutrition, lo studio della Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista a Salerno. Percorsi nutrizionali personalizzati, visite in studio e consulenze online. Prenota la tua consulenza.",
    path: "/",
  });

  const sections = settings.sections || [];
  const renderers = {
    hero: (key) => <HeroSection key={key} settings={settings} />,
    intro: (key) => <IntroSection key={key} settings={settings} />,
    services: (key) => <ServicesSection key={key} settings={settings} services={services} />,
    method: (key) => <MethodSection key={key} settings={settings} />,
    cta: (key) => <CtaSection key={key} settings={settings} />,
  };

  return (
    <>
      {sections.map((sec) => {
        if (!sec.visible) return null;
        if (sec.type === "custom") return <CustomBlock key={sec.id} block={sec} />;
        const render = renderers[sec.type];
        return render ? render(sec.id) : null;
      })}
      <ContactStrip settings={settings} />
    </>
  );
}
